import Link from 'next/link'
import type { User, Market, EventSummary, LeaderboardList, CasinoList, UserListItem } from '@/lib/api'
import CreateMarketButton from '../CreateMarketButton'
import NavSearch from '../NavSearch'
import NotificationBell from '../NotificationBell'
import FancyLogo from '../FancyLogo'
import SidebarTrigger from '../SidebarTrigger'
import NavDropdown from '../NavDropdown'
import GamesMenu from './GamesMenu'
import { GAMES } from './games'

const LeaderboardIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M6 10a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1zM10 6a1 1 0 0 1 1 1v8a1 1 0 1 1-2 0V7a1 1 0 0 1 1-1zM14 2a1 1 0 0 1 1 1v12a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1z" />
  </svg>
)

export interface NavRow1Props {
  user: User | null
  markets: Market[]
  events: EventSummary[]
  categories: string[]
  leaderboardList: LeaderboardList
  casinoList: CasinoList
  users: UserListItem[]
  loginUrl: string
  /** Show the mobile sidebar toggle. False in the embedded widget (no sidebar there). */
  showSidebarTrigger?: boolean
}

/**
 * The shared top row of the global nav: [☰ Logo] … search … [create · icons · user].
 *
 * This is the single source of truth for Row 1 across both the core app (rendered by
 * `Navbar`) and the embedded casino widget (rendered by `src/widget/nav-widget.tsx`). It is
 * purely presentational — it receives all data as props and does no fetching — so the same
 * markup compiles into both consumers and cannot drift between the two services (epic #33).
 *
 * The Next-specific imports here (`next/link`, and the children's `next/image` / server
 * actions) are rewritten to framework-neutral shims by the widget's esbuild build; in the
 * core app they keep their normal Next behavior.
 */
export default function NavRow1({
  user,
  markets,
  events,
  categories,
  leaderboardList,
  casinoList,
  users,
  loginUrl,
  showSidebarTrigger = true,
}: NavRow1Props) {
  const iconLinkClass = "rounded-full p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"

  return (
    <div className="flex items-center gap-2 py-3">
      {/* Left: sidebar toggle + logo */}
      {showSidebarTrigger && <SidebarTrigger />}
      <FancyLogo currentUsername={user?.discord_username ?? null} />

      {/* Desktop middle: search */}
      <div className="hidden sm:flex flex-1 items-center mx-2">
        <NavSearch markets={markets} events={events} currentUserId={user?.id ?? null} leaderboardList={leaderboardList} casinoList={casinoList} users={users} isAdmin={user?.is_admin ?? false} />
      </div>

      {/* Right: action icons + user menu */}
      <div className="ml-auto flex items-center gap-1">
        {/* Create market */}
        <span>
          <CreateMarketButton isLoggedIn={!!user} loginUrl={loginUrl} balance={user?.balance ?? null} categories={categories} />
        </span>

        <Link href="/leaderboard" title="Leaderboard" className={iconLinkClass}>
          <LeaderboardIcon />
        </Link>
        {/* Mobile: collapse Casino/FDL/Risk behind a single globe icon */}
        <div className="sm:hidden">
          <GamesMenu iconLinkClass={iconLinkClass} />
        </div>
        {/* Wider screens: show all three game icons directly */}
        <div className="hidden sm:flex items-center gap-1">
          {GAMES.map(({ href, title, Icon }) => (
            <Link key={href} href={href} title={title} className={iconLinkClass}>
              <Icon />
            </Link>
          ))}
        </div>
        {user && <NotificationBell />}

        {user ? (
          <NavDropdown
            username={user.discord_username}
            displayName={user.display_name}
            avatarUrl={user.avatar_url}
            balance={user.balance}
            isAdmin={user.is_admin}
          />
        ) : (
          <a href={loginUrl} className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors">
            Login
          </a>
        )}
      </div>
    </div>
  )
}
